import moment from 'moment'
import type { Moment } from 'moment'
export const getDefaultDate = () => {
  const now = new Date()
  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1)
  return {
    year: lastMonth.getFullYear(),
    month: lastMonth.getMonth() + 1,
  }
}

// Calculate default month (last month)
export const getDefaultMonthKey = (): string => {
  const now = new Date()
  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1)
  const month = lastMonth.getMonth() + 1
  const year = lastMonth.getFullYear()
  return `${String(month).padStart(2, '0')}-${year}`
}

// Helper function to safely normalize month key to string format (MM-YYYY)
export const normalizeMonthKey = (value: unknown): string => {
  // If value is already a valid string, check and convert format if needed
  if (typeof value === 'string' && value.trim() !== '') {
    const trimmed = value.trim()
    // Check if it's in YYYY-MM format (e.g., "2025-11")
    const parts = trimmed.split('-')
    if (parts.length === 2) {
      const firstPart = parseInt(parts[0], 10)
      const secondPart = parseInt(parts[1], 10)
      // If first part is 4 digits (year) and second is 1-12 (month), convert to MM-YYYY
      if (
        !isNaN(firstPart) &&
        !isNaN(secondPart) &&
        firstPart >= 2000 &&
        firstPart <= 2100 &&
        secondPart >= 1 &&
        secondPart <= 12
      ) {
        // Convert from YYYY-MM to MM-YYYY
        return `${String(secondPart).padStart(2, '0')}-${firstPart}`
      }
      // If first part is 1-12 (month) and second is 4 digits (year), it's already MM-YYYY
      if (
        !isNaN(firstPart) &&
        !isNaN(secondPart) &&
        firstPart >= 1 &&
        firstPart <= 12 &&
        secondPart >= 2000 &&
        secondPart <= 2100
      ) {
        return trimmed
      }
    }
    // If format doesn't match expected patterns, return as-is (will be validated later)
    return trimmed
  }
  // If value is null, undefined, or empty, return default
  if (value == null || value === '') {
    return getDefaultMonthKey()
  }
  // If value is a number, try to convert it (though this shouldn't happen)
  if (typeof value === 'number') {
    return getDefaultMonthKey()
  }
  // For any other type, return default
  return getDefaultMonthKey()
}

// Helper function to format number with comma as decimal separator
export const formatNumber = (value: number | undefined): string => {
  if (value == null || isNaN(value)) {
    return '0,00'
  }
  return value.toFixed(2).replace('.', ',')
}

// Helper function to format date from Moment to Polish locale string
export const formatDate = (
  date: Moment | Date | string | null | undefined,
): string => {
  if (!date) {
    return ''
  }
  // The API sends lastCalculationDate as an ISO string, so accept anything
  // moment can parse - not just an already-built Moment.
  const parsed = moment.isMoment(date) ? date : moment(date)
  if (!parsed.isValid()) {
    return ''
  }
  return parsed.format('DD.MM.YYYY HH:mm')
}

// Format date from selected month (MM-YYYY) to YYYY-MM-01
export const formatDateFromMonth = (monthKey: string): string => {
  if (!monthKey) {
    const defaultMonth = getDefaultMonthKey()
    const [month, year] = defaultMonth.split('-').map(Number)
    return `${year}-${String(month).padStart(2, '0')}-01`
  }
  const [month, year] = monthKey.split('-').map(Number)
  return `${year}-${String(month).padStart(2, '0')}-01`
}

